import { useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';

import { JobPosition } from '@pflegia/data-access';
import { RegisterOrigin } from '@pflegia/types/api/candidate/register-origin';
import { EmployeeHeader } from '@pflegia/ui/funnel/components/employee-header/EmployeeHeader.component';
import { ReactComponent as CheckCircle } from '@pflegia/ui/icons/check-circle.svg';

import EmployeeImage from '@public/assets/employee.jpg';

import { FUNNEL } from '@shared/application/constants';
import { MAGAZIN_UTM_CAMPAIGN, MAGAZIN_UTM_CONTENT, getMagazinUtmRecord } from '@shared/utils/magazin';

const ButtonLink = dynamic(() => import('@pflegia/ui/shared/button-link/ButtonLink'));
const SignUpButton = dynamic(() => import('../sign-up-dialogue/SignUpButton.component'));

export interface CareerProps {
  jobPosition?: Pick<JobPosition, 'geoLocation' | 'id' | 'slug' | 'title'>;
  articleSlug?: string;
  articleCategory?: string;
}

const Career: React.FC<CareerProps> = ({ jobPosition, articleSlug, articleCategory }) => {
  const { t } = useTranslation('career');
  const bullets = t('bullets', { returnObjects: true }) as string[];

  const funnelHref =
    articleSlug || articleCategory
      ? {
          pathname: FUNNEL,
          query: {
            registerOrigin: RegisterOrigin.Blog,
            ...getMagazinUtmRecord(
              { articleSlug, category: articleCategory },
              MAGAZIN_UTM_CONTENT.careerAdviceRequest,
              MAGAZIN_UTM_CAMPAIGN.registration,
            ),
          },
        }
      : FUNNEL;

  return (
    <div className='bg-extended-jobs-background my-4 rounded-2xl p-4 pt-6 lg:p-12'>
      <div className='mb-6 flex flex-col items-center justify-between sm:flex-row'>
        <EmployeeHeader
          image={EmployeeImage}
          className='flex flex-col gap-0 text-center sm:flex-row sm:gap-4 sm:text-left'
          imageClassName='h-40 w-40 sm:h-20 sm:w-20'
          nameClassName='text-lg font-semibold mb-0!'
        />
        {jobPosition ? (
          <SignUpButton
            jobPositionAttributes={{
              geoLocation: jobPosition.geoLocation,
              id: jobPosition.id,
              jobTitle: jobPosition.title,
              slug: jobPosition.slug,
            }}
            label={t('applyButtonLabel')}
          />
        ) : (
          <ButtonLink className='bg-primary rounded-full px-8 py-3 font-medium text-white hover:shadow-lg xl:px-12' href={funnelHref}>
            {t('applyButtonLabel')}
          </ButtonLink>
        )}
      </div>

      <div className='border-t border-gray-200 py-5'>
        <h3 className='text-default my-0! font-bold! sm:text-xl! md:text-2xl!'>{t('title')}</h3>
        <span>{t('description')}</span>

        <ul className='mt-6! space-y-4! px-0!'>
          {bullets.map(bullet => (
            <li key={bullet} className='flex items-start'>
              <CheckCircle className='text-primary mr-2 h-5 w-5 shrink-0' />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Career;
